import { z } from 'zod';
/**
 * In-app alert aggregation settings (KIR-3253).
 *
 * Configures the per-severity aggregation windows (in minutes) and the
 * "Suppress Repeated Summaries" behavior that the agent and IDE extension
 * apply to in-app alert notifications (mechanism built in KIR-2659).
 *
 * Scope is implicit from the endpoint called (org vs. personal/singleton),
 * mirroring the notification-settings contract — `organizationId` is NOT part
 * of the response.
 */
/**
 * A per-severity aggregation window, in minutes.
 *
 * - Whole numbers only (no decimals).
 * - 0 = real-time for that severity (no window opens; every alert shows
 *   immediately and no summary is generated).
 * - Max 120 minutes.
 */
const INTERVAL_MESSAGE = 'Enter a whole number from 0 to 120';
export const aggregationIntervalSchema = z
    .number({ error: INTERVAL_MESSAGE })
    .int(INTERVAL_MESSAGE)
    .min(0, INTERVAL_MESSAGE)
    .max(120, INTERVAL_MESSAGE);
/**
 * The aggregation mode is DERIVED from the full configuration (severity values
 * + suppress toggle); it is never stored. Use {@link deriveAggregationMode}.
 */
export var AggregationMode;
(function (AggregationMode) {
    AggregationMode["RealTime"] = "real-time";
    AggregationMode["Balanced"] = "balanced";
    AggregationMode["Quiet"] = "quiet";
    AggregationMode["Custom"] = "custom";
})(AggregationMode || (AggregationMode = {}));
const aggregationFields = {
    criticalMinutes: aggregationIntervalSchema,
    highMinutes: aggregationIntervalSchema,
    mediumMinutes: aggregationIntervalSchema,
    lowMinutes: aggregationIntervalSchema,
    suppressRepeatedSummaries: z.boolean(),
};
/**
 * Shared invariant: when every severity window is 0 (Real-time), no summaries
 * are ever generated, so "Suppress Repeated Summaries" must be OFF. Enforced
 * on both the response and the update body so the client and server agree.
 */
function enforceRealtimeSuppressOff(cfg, ctx) {
    const allZero = cfg.criticalMinutes === 0 &&
        cfg.highMinutes === 0 &&
        cfg.mediumMinutes === 0 &&
        cfg.lowMinutes === 0;
    if (allZero && cfg.suppressRepeatedSummaries) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['suppressRepeatedSummaries'],
            message: 'Suppress Repeated Summaries must be off when all severity windows are 0 (Real-time).',
        });
    }
}
/**
 * Request body for updating in-app alert aggregation settings.
 * Used for both personal and organization endpoints.
 */
export const updateAlertAggregationSettingsBodySchema = z
    .object(aggregationFields)
    .superRefine(enforceRealtimeSuppressOff);
/**
 * Response for in-app alert aggregation settings.
 * Used for both personal and organization endpoints.
 */
export const alertAggregationSettingsResponseSchema = z
    .object({
    ...aggregationFields,
    updatedAt: z.coerce.date().optional(),
})
    .superRefine(enforceRealtimeSuppressOff);
// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------
/** Real-time: every alert shows immediately, no aggregation, suppress off. */
export const REALTIME_PRESET = {
    criticalMinutes: 0,
    highMinutes: 0,
    mediumMinutes: 0,
    lowMinutes: 0,
    suppressRepeatedSummaries: false,
};
/**
 * Balanced (recommended) — the default configuration. "Reset to Defaults"
 * reverts to these values.
 */
export const BALANCED_DEFAULT = {
    criticalMinutes: 2,
    highMinutes: 5,
    mediumMinutes: 10,
    lowMinutes: 20,
    suppressRepeatedSummaries: true,
};
/** Quiet: longer windows, fewer interruptions. */
export const QUIET_PRESET = {
    criticalMinutes: 5,
    highMinutes: 15,
    mediumMinutes: 30,
    lowMinutes: 60,
    suppressRepeatedSummaries: true,
};
// ---------------------------------------------------------------------------
// Mode derivation
// ---------------------------------------------------------------------------
function severityValuesMatch(a, b) {
    return (a.criticalMinutes === b.criticalMinutes &&
        a.highMinutes === b.highMinutes &&
        a.mediumMinutes === b.mediumMinutes &&
        a.lowMinutes === b.lowMinutes);
}
/**
 * Derive the aggregation mode from a full configuration (severity values +
 * suppress toggle).
 *
 * - All four windows 0 → Real-time (suppress is forced off in this state).
 * - Balanced/Quiet severity values WITH suppress ON → that preset.
 *   (Matching values but suppress OFF → Custom, per spec.)
 * - Anything else → Custom.
 */
export function deriveAggregationMode(config) {
    if (config.criticalMinutes === 0 &&
        config.highMinutes === 0 &&
        config.mediumMinutes === 0 &&
        config.lowMinutes === 0) {
        return AggregationMode.RealTime;
    }
    if (severityValuesMatch(config, BALANCED_DEFAULT) &&
        config.suppressRepeatedSummaries) {
        return AggregationMode.Balanced;
    }
    if (severityValuesMatch(config, QUIET_PRESET) &&
        config.suppressRepeatedSummaries) {
        return AggregationMode.Quiet;
    }
    return AggregationMode.Custom;
}
//# sourceMappingURL=alert-aggregation.js.map